import React from 'react';
import { useTheme } from 'styled-components';
import { formatNZD, useSegment } from '@sparknz/journeys-common';
import { Text } from '@sparknz/spark-pattern-library';

import { BottomBarMpdPromotionsQuery$data } from '__generated__/BottomBarMpdPromotionsQuery.graphql';
import { useSelectedPlans } from '../../providers';
import { getEligiblePromotionText } from '../../utils/getEligiblePromotionText.utils';
import { pluralize } from '../../utils/pluralize';

interface ExtractDisplayPricesParameter {
  mpdPromotions: BottomBarMpdPromotionsQuery$data['mpdPromotions'];
  isBusiness: boolean;
}

function extractDisplayPrices({
  isBusiness,
  mpdPromotions,
}: ExtractDisplayPricesParameter) {
  const {
    basePrice,
    discount,
    effectivePrice,
    effectivePriceExcludingTax,
    basePriceExcludingTax,
  } = mpdPromotions?.price[0];

  if (isBusiness) {
    return {
      basePrice: basePriceExcludingTax,
      effectivePrice: effectivePriceExcludingTax,
      discountPercentage: discount?.appliedValueExcludingTax,
      discountedValue: discount?.discountedValueExcludingTax,
    };
  }

  return {
    basePrice,
    effectivePrice,
    discountPercentage: discount?.appliedValue,
    discountedValue: discount?.discountedValue,
  };
}

interface BottomBarSelectionDetailsProps {
  mpdPromotions: BottomBarMpdPromotionsQuery$data['mpdPromotions'];
  showTeamUpPromotion?: boolean;
}

export default function BottomBarSelectionDetails({
  mpdPromotions,
  showTeamUpPromotion,
}: BottomBarSelectionDetailsProps) {
  const { palette } = useTheme();
  const {
    totalSelectedPlansQuantity,
    totalSelectedTeamUpEligiblePlansQuantity,
  } = useSelectedPlans();
  const { isBusiness } = useSegment();

  const { basePrice, discountPercentage, discountedValue, effectivePrice } =
    extractDisplayPrices({
      isBusiness,
      mpdPromotions,
    });

  const plansQuantityText = `${totalSelectedPlansQuantity} ${pluralize({
    quantity: totalSelectedPlansQuantity,
    word: 'plan',
  })}:`;

  const teamUpPromotionText = showTeamUpPromotion
    ? getEligiblePromotionText(
        totalSelectedTeamUpEligiblePlansQuantity,
        totalSelectedPlansQuantity,
      )
    : '';

  if (!(effectivePrice && basePrice)) {
    throw new Error('Missing tax exclusive display prices');
  }

  if (discountPercentage && discountedValue) {
    return (
      <div>
        <Text component="span" variant="bodySDemi">
          {plansQuantityText}&nbsp;
        </Text>
        <Text component="span" variant="bodyMMedium">
          {formatNZD(effectivePrice)}&nbsp;
          <span style={{ color: palette.baseColors.mawheranui }}>
            <s>{formatNZD(basePrice)}</s>
          </span>
        </Text>
        {isBusiness ? (
          <Text
            color={palette.baseColors.mawheranui}
            component="span"
            variant="bodySDemi"
          >
            &nbsp;(excl. GST)
          </Text>
        ) : null}
        <Text variant="bodySDemi" color={palette.baseColors.mawhero}>
          {discountPercentage}% off eligible plans:&nbsp;
          {formatNZD(discountedValue)} savings
        </Text>
      </div>
    );
  }

  return (
    <div>
      <Text component="span" variant="bodySDemi">
        {plansQuantityText}&nbsp;
      </Text>
      <Text component="span" variant="bodyMMedium">
        {formatNZD(basePrice)}
      </Text>
      {teamUpPromotionText ? (
        <Text variant="bodySDemi" color={palette.baseColors.mawhero}>
          {teamUpPromotionText}
        </Text>
      ) : null}
    </div>
  );
}
